import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Building2, Briefcase, Loader2, Home, Clock, Calendar, AlertTriangle } from 'lucide-react';

interface Job {
  job_id: number;
  job_name: string;
  bradanna_job_number: string;
  address?: string;
  city: string;
  state?: string;
  zip_code?: string;
  general_contractor?: string;
  project_manager?: string;
  lat: number;
  lng: number;
  status?: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export const TruckDriverJobsList: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // API call function
  const apiCall = async (endpoint: string) => {
    const token = localStorage.getItem('bfa_token');

    if (!token) {
      throw new Error('No authentication token');
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 401) {
      localStorage.removeItem('bfa_token');
      localStorage.removeItem('bfa_user');
      window.location.href = '/login';
      throw new Error('Authentication expired');
    }

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || `API request failed: ${response.status}`);
    }

    return response.json();
  };

  // Load jobs from API
  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await apiCall('/api/jobs');

      if (response.success && response.data) {
        // Filter out test/demo jobs only
        const regularJobs = response.data.filter((job: Job) =>
          !job.job_name.toLowerCase().includes('test') &&
          !job.job_name.toLowerCase().includes('demo')
        );

        setJobs(regularJobs);
        setFilteredJobs(regularJobs);
      }
    } catch (err: any) {
      console.error('Failed to load jobs:', err);
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort jobs based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredJobs(jobs);
      return;
    }

    const searchLower = searchTerm.toLowerCase();

    const filtered = jobs.filter(job => {
      return (
        (job.job_name && job.job_name.toLowerCase().includes(searchLower)) ||
        (job.bradanna_job_number && job.bradanna_job_number.toLowerCase().includes(searchLower)) ||
        (job.city && job.city.toLowerCase().includes(searchLower)) ||
        (job.address && job.address.toLowerCase().includes(searchLower)) ||
        (job.general_contractor && job.general_contractor.toLowerCase().includes(searchLower))
      );
    });

    // Sort results: name starts with > name contains > job number > other
    const sorted = filtered.sort((a, b) => {
      const aStartsWithName = a.job_name?.toLowerCase().startsWith(searchLower);
      const bStartsWithName = b.job_name?.toLowerCase().startsWith(searchLower);
      if (aStartsWithName && !bStartsWithName) return -1;
      if (!aStartsWithName && bStartsWithName) return 1;

      const aContainsName = a.job_name?.toLowerCase().includes(searchLower);
      const bContainsName = b.job_name?.toLowerCase().includes(searchLower);
      if (aContainsName && !bContainsName) return -1;
      if (!aContainsName && bContainsName) return 1;

      const aJobNumMatch = a.bradanna_job_number?.toLowerCase().includes(searchLower);
      const bJobNumMatch = b.bradanna_job_number?.toLowerCase().includes(searchLower);
      if (aJobNumMatch && !bJobNumMatch) return -1;
      if (!aJobNumMatch && bJobNumMatch) return 1;

      const aNum = parseInt(a.bradanna_job_number?.split('-')[1] || '0', 10);
      const bNum = parseInt(b.bradanna_job_number?.split('-')[1] || '0', 10);
      return bNum - aNum;
    });

    setFilteredJobs(sorted);
  }, [searchTerm, jobs]);

  // Load jobs on component mount
  useEffect(() => {
    loadJobs();
  }, []);

  const handleJobClick = (jobId: number) => {
    navigate(`/jobs/${jobId}`);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#f0f2f5'
    },
    headerSection: {
      backgroundColor: '#2c2c3e',
      color: 'white',
      padding: '20px',
      paddingTop: '16px'
    },
    headerTop: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '12px'
    },
    headerContent: {
      flex: 1
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: 'white',
      margin: 0
    },
    subtitle: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.7)',
      margin: '4px 0 0 0'
    },
    countBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 14px',
      backgroundColor: 'rgba(99, 102, 241, 0.3)',
      borderRadius: '20px',
      fontSize: '13px',
      fontWeight: '600',
      color: 'white',
      marginTop: '12px'
    },
    searchSection: {
      padding: '16px 20px',
      backgroundColor: 'white',
      borderBottom: '1px solid #e5e7eb',
      position: 'sticky' as const,
      top: 0,
      zIndex: 10
    },
    searchContainer: {
      position: 'relative' as const
    },
    searchInput: {
      width: '100%',
      padding: '14px 16px 14px 48px',
      fontSize: '16px',
      border: '2px solid #e5e7eb',
      borderRadius: '12px',
      backgroundColor: '#f9fafb',
      outline: 'none',
      transition: 'all 0.2s'
    },
    searchIcon: {
      position: 'absolute' as const,
      left: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#9ca3af'
    },
    contentSection: {
      padding: '16px 20px',
      paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))'
    },
    jobsList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '12px'
    },
    jobCard: {
      backgroundColor: 'white',
      borderRadius: '16px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      border: '1px solid #e5e7eb'
    },
    jobName: {
      fontSize: '17px',
      fontWeight: '600',
      color: '#1f2937',
      margin: 0,
      lineHeight: '1.3'
    },
    jobNumber: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      color: '#3b82f6',
      fontWeight: '600',
      backgroundColor: '#eff6ff',
      padding: '4px 10px',
      borderRadius: '6px',
      marginBottom: '12px'
    },
    jobDetails: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px'
    },
    jobLocation: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#6b7280'
    },
    jobContractor: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#6b7280'
    },
    iconWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      backgroundColor: '#f3f4f6',
      borderRadius: '8px',
      flexShrink: 0
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: '16px'
    },
    loadingSpinner: {
      color: '#3b82f6',
      animation: 'spin 1s linear infinite'
    },
    loadingText: {
      fontSize: '16px',
      color: '#6b7280',
      fontWeight: '500'
    },
    error: {
      margin: '20px',
      padding: '20px',
      fontSize: '15px',
      color: '#dc2626',
      backgroundColor: '#fef2f2',
      borderRadius: '12px',
      border: '1px solid #fecaca',
      textAlign: 'center' as const
    },
    noJobs: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      backgroundColor: 'white',
      borderRadius: '16px',
      border: '1px solid #e5e7eb'
    },
    noJobsIcon: {
      width: '64px',
      height: '64px',
      backgroundColor: '#f3f4f6',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '16px'
    },
    noJobsText: {
      fontSize: '16px',
      color: '#6b7280',
      fontWeight: '500',
      margin: 0
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Loader2 size={40} style={styles.loadingSpinner} />
          <p style={styles.loadingText}>Loading jobs...</p>
        </div>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Dark Header Section */}
      <div style={styles.headerSection}>
        <div style={styles.headerTop}>
          <div style={styles.headerContent}>
            <h1 style={styles.title}>Jobs</h1>
            <p style={styles.subtitle}>All Active Projects</p>
          </div>
        </div>
        <div style={styles.countBadge}>
          <Briefcase size={14} />
          {filteredJobs.length} jobs
        </div>
      </div>

      {/* Sticky Search Section */}
      <div style={styles.searchSection}>
        <div style={styles.searchContainer}>
          <Search size={20} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by name, number, or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      {/* Jobs List */}
      <div style={styles.contentSection}>
        <div style={styles.jobsList}>
          {filteredJobs.length === 0 ? (
            <div style={styles.noJobs}>
              <div style={styles.noJobsIcon}>
                <Briefcase size={28} color="#9ca3af" />
              </div>
              <p style={styles.noJobsText}>
                {searchTerm ? 'No jobs match your search' : 'No jobs found'}
              </p>
            </div>
          ) : (
            filteredJobs.map((job) => (
              <div
                key={job.job_id}
                style={styles.jobCard}
                onClick={() => handleJobClick(job.job_id)}
              >
                <h3 style={styles.jobName}>{job.job_name}</h3>
                <div style={styles.jobNumber}>
                  Job # {job.bradanna_job_number}
                </div>
                <div style={styles.jobDetails}>
                  <div style={styles.jobLocation}>
                    <div style={styles.iconWrapper}>
                      <MapPin size={14} color="#6b7280" />
                    </div>
                    <span>{job.address ? `${job.address}, ` : ''}{job.city}{job.state ? `, ${job.state}` : ''}</span>
                  </div>
                  {job.general_contractor && (
                    <div style={styles.jobContractor}>
                      <div style={styles.iconWrapper}>
                        <Building2 size={14} color="#6b7280" />
                      </div>
                      <span>{job.general_contractor}</span>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* CDL Driver Bottom Navigation */}
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-around',
        alignItems: 'center',
        padding: '8px 0 12px',
        zIndex: 1000,
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          <Home size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Home</span>
        </button>

        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          <Clock size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Full Log</span>
        </button>

        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          <Calendar size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Weekly Hrs</span>
        </button>

        <button
          onClick={() => navigate('/equipment-issue')}
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#ef4444'
          }}
        >
          <AlertTriangle size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Report Issue</span>
        </button>

        <button
          style={{
            background: 'none',
            border: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '4px',
            padding: '8px 12px',
            cursor: 'pointer',
            color: '#3b82f6'
          }}
        >
          <Briefcase size={24} />
          <span style={{ fontSize: '11px', fontWeight: '600' }}>Jobs</span>
        </button>
      </nav>
    </div>
  );
};

export default TruckDriverJobsList;
